import { Check, Send, X } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { PortalFirmAPI } from "../api/companiesApi";
import {
  refreshCompanies,
  useCaseApprovals,
  useCaseMessages
} from "../queries";
import type { Company, CompanyApproval } from "../types";

const money = (n: number | null) =>
  n == null
    ? ""
    : n.toLocaleString(undefined, { style: "currency", currency: "USD" });

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      })
    : "";

const STATUS: Record<
  CompanyApproval["status"],
  { label: string; tone: string }
> = {
  draft: { label: "Not sent", tone: "bg-fill-2 text-fg-3" },
  pending: {
    label: "Waiting on HR",
    tone: "bg-warn-soft text-warn"
  },
  approved: {
    label: "Approved",
    tone: "bg-success-soft text-success"
  },
  declined: {
    label: "Declined",
    tone: "bg-danger-soft text-danger"
  },
  cancelled: { label: "Withdrawn", tone: "bg-fill-2 text-fg-3" }
};

const act = async (run: () => Promise<unknown>, done: string) => {
  try {
    await run();
    toast(done);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Couldn't do that");
  } finally {
    void refreshCompanies();
  }
};

const ApprovalRow: FC<{ company: Company; approval: CompanyApproval }> = ({
  company,
  approval: a
}) => (
  <div
    className="flex flex-col gap-1 rounded-md border border-line-2 p-2.5"
    data-testid={`approval-${a.id}`}
  >
    <span className="flex items-start gap-2">
      <span className="flex-1 text-base leading-4.5">
        {a.summary}
        {a.amount != null ? (
          <span className="text-fg-3"> · {money(a.amount)}</span>
        ) : null}
      </span>
      <Badge className={cn("shrink-0 font-normal", STATUS[a.status].tone)}>
        {STATUS[a.status].label}
      </Badge>
    </span>
    {a.rule_label ? (
      <span className="text-sm text-fg-3">{a.rule_label}</span>
    ) : null}
    {a.answered_by ? (
      <span className="text-sm text-fg-3">
        {a.answered_by.name} · {when(a.answered_by.at)}
        {a.answer_note ? `: “${a.answer_note}”` : ""}
      </span>
    ) : a.sent_at ? (
      <span className="text-sm text-fg-3">Sent {when(a.sent_at)}</span>
    ) : null}
    {a.status === "draft" || a.status === "pending" ? (
      <span className="flex gap-1.5 pt-1">
        {a.status === "draft" ? (
          <Button
            variant="primary"
            size="xs"
            onClick={() =>
              void act(
                () => PortalFirmAPI.sendApproval(company.id, a.id),
                `Sent to ${company.name} HR`
              )
            }
          >
            <Send /> Send to HR
          </Button>
        ) : null}
        <Button
          size="xs"
          variant="ghost"
          onClick={() =>
            void act(
              () => PortalFirmAPI.cancelApproval(company.id, a.id),
              "Withdrawn"
            )
          }
        >
          <X /> {a.status === "draft" ? "Dismiss" : "Withdraw"}
        </Button>
      </span>
    ) : null}
  </div>
);

/** HR portal approvals and messages for one case, on the case's Company panel. */
export const CaseHrSection: FC<{ company: Company; individualId: string }> = ({
  company,
  individualId
}) => {
  const approvals = useCaseApprovals(company.id, individualId).data ?? [];
  const thread = useCaseMessages(company.id, individualId).data;
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const messages = thread?.messages ?? [];
  const canReply = (thread?.recipients ?? 0) > 0;
  if (!approvals.length && !messages.length && !canReply) return null;

  const send = async () => {
    setSending(true);
    try {
      await PortalFirmAPI.reply(company.id, individualId, reply.trim());
      setReply("");
      toast(`Sent to ${company.name} HR`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't send the message"
      );
    } finally {
      setSending(false);
      void refreshCompanies();
    }
  };

  return (
    <div
      className="flex flex-col gap-2 border-b border-line-2 px-4 py-3"
      data-testid="case-hr"
    >
      <span className="text-sm font-medium text-fg-3">{company.name} HR</span>
      {approvals.map((a) => (
        <ApprovalRow key={a.id} company={company} approval={a} />
      ))}
      {messages.length ? (
        <div className="flex flex-col gap-1.5">
          {messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                "rounded-md px-2.5 py-1.5 text-sm leading-4.5",
                m.author.kind === "contact"
                  ? "bg-fill-2"
                  : "border border-line-2"
              )}
            >
              <span className="flex gap-2 text-sm text-fg-3">
                <span className="font-medium text-fg">{m.author.name}</span>
                {when(m.created_at)}
              </span>
              <span className="whitespace-pre-wrap">{m.body}</span>
            </div>
          ))}
        </div>
      ) : null}
      {canReply ? (
        <div className="flex flex-col gap-1.5">
          <Textarea
            rows={2}
            value={reply}
            aria-label={`Message ${company.name} HR`}
            placeholder={`Message ${company.name} HR about this employee`}
            onChange={(e) => setReply(e.currentTarget.value)}
            className="text-base"
          />
          <span className="flex justify-end">
            <Button
              variant="primary"
              size="xs"
              disabled={!reply.trim() || sending}
              onClick={() => void send()}
            >
              <Check /> Send
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  );
};
